<template>
  <div
    v-if="open"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
    @click.self="$emit('close')"
  >
    <div class="bg-white rounded-md shadow-lg w-full max-w-md p-4">
      <h3 class="text-base font-semibold mb-2">{{ title }}</h3>
      <div class="text-xs text-gray-600 mb-3">
        Expense <span class="font-mono">#{{ expense ? expense.id : '' }}</span>
        <span v-if="expense">— {{ expense.description }}</span>
      </div>

      <div v-if="mode === 'override_status'" class="mb-3">
        <label class="block text-xs text-gray-500 mb-0.5">New status</label>
        <select v-model="newStatus" class="w-full h-9 px-2 border border-gray-400 rounded-md text-sm">
          <option v-for="s in statusOptions" :key="s.value" :value="s.value">{{ s.label }}</option>
        </select>
      </div>

      <div v-if="mode === 'reassign_manager'" class="mb-3">
        <label class="block text-xs text-gray-500 mb-0.5">New manager</label>
        <select v-model="newManagerId" class="w-full h-9 px-2 border border-gray-400 rounded-md text-sm">
          <option :value="null">— None —</option>
          <option v-for="u in users" :key="u.id" :value="u.id">{{ userLabel(u) }}</option>
        </select>
      </div>

      <div class="mb-3">
        <label class="block text-xs text-gray-500 mb-0.5">Reason (required)</label>
        <textarea
          v-model="reason"
          rows="3"
          class="w-full px-2 py-1 border border-gray-400 rounded-md text-sm"
          placeholder="Recorded in the expense's audit log."
        ></textarea>
      </div>

      <div v-if="error" class="text-xs text-red-700 mb-2">{{ error }}</div>

      <div class="flex items-center justify-end gap-2">
        <button
          class="px-3 py-1 text-sm border border-gray-400 rounded hover:bg-gray-100 disabled:opacity-50"
          :disabled="working"
          @click="$emit('close')"
        >Cancel</button>
        <button
          class="px-3 py-1 text-sm rounded text-white disabled:opacity-50"
          :class="confirmClass"
          :disabled="working || !canConfirm"
          @click="apply"
        >{{ working ? 'Working...' : confirmLabel }}</button>
      </div>
    </div>
  </div>
</template>

<script>
export default { name: "admin-reason-modal" };
</script>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from "vue";
import { useStore } from "vuex";
import {
  adminOverrideStatus, adminReassignManager,
  adminSoftDelete, adminRestore,
} from "@/services/Expense.js";

const props = defineProps({
  open: { type: Boolean, default: false },
  mode: { type: String, default: "override_status" },
  expense: { type: Object, default: null },
  users: { type: Array, default: () => [] },
});
const emit = defineEmits(["close", "applied"]);

const store = useStore();

const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted — Pending Manager Approval' },
  { value: 'manager_approved', label: 'Manager Approved — Pending Accounting' },
  { value: 'accounting_approved', label: 'Accounting Approved' },
  { value: 'closed', label: 'Closed' },
  { value: 'rejected', label: 'Rejected — Returned to Submitter' },
];

const newStatus = ref('draft');
const newManagerId = ref(null);
const reason = ref('');
const working = ref(false);
const error = ref('');

watch(() => [props.open, props.expense, props.mode], () => {
  if (props.open) {
    reason.value = '';
    error.value = '';
    if (props.expense) {
      newStatus.value = props.expense.status || 'draft';
      newManagerId.value = props.expense.manager ? props.expense.manager.id : null;
    }
  }
});

const title = computed(() => {
  if (props.mode === 'override_status') return 'Override status';
  if (props.mode === 'reassign_manager') return 'Reassign manager';
  if (props.mode === 'soft_delete') return 'Delete record';
  if (props.mode === 'restore') return 'Restore record';
  return 'Admin action';
});

const confirmLabel = computed(() => {
  if (props.mode === 'soft_delete') return 'Delete';
  if (props.mode === 'restore') return 'Restore';
  return 'Apply';
});

const confirmClass = computed(() => {
  if (props.mode === 'soft_delete') return 'bg-red-600 hover:bg-red-700';
  return 'bg-accent-primary hover:bg-accent-secondary';
});

const canConfirm = computed(() => !!reason.value.trim());

function userLabel(u) {
  const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  return name || u.username || `User #${u.id}`;
}

async function apply() {
  if (!props.expense) return;
  const id = props.expense.id;
  const token = store.state.auth.token;
  const r = reason.value.trim();
  working.value = true;
  error.value = '';
  try {
    let updated;
    if (props.mode === 'override_status') {
      updated = await adminOverrideStatus(token, id, newStatus.value, r);
    } else if (props.mode === 'reassign_manager') {
      updated = await adminReassignManager(token, id, newManagerId.value, r);
    } else if (props.mode === 'soft_delete') {
      updated = await adminSoftDelete(token, id, r);
    } else if (props.mode === 'restore') {
      updated = await adminRestore(token, id, r);
    }
    emit('applied', updated);
    emit('close');
  } catch (e) {
    error.value = e?.response?.data?.detail || 'Action failed.';
  }
  working.value = false;
}
</script>
